import React, { useState, useEffect, useMemo, useRef } from 'react';
import type { Athlete, Course, UserProfile, GroupRideDetails, SpiritedSegment } from '../types';
import { calculateLapsForAthlete } from '../services/raceAnalysis';
import {
    formatSectorTime,
    getSectorAthleteTelemetry,
    SectorAthleteTelemetry,
    SectorLeaderboardRow
} from '../utils/raceSector';
import { ArrowLeftIcon } from './icons/PlaybackIcons';
import { renderMarkdown } from '../utils/markdownRenderer';

import {
    AthleteRaceContext,
    AthleteRankContext,
    generateAthletePrompt,
    generateGroupRidePrompt
} from '../utils/insightPrompts';

export interface GeminiAthleteInsightsProps {
    athleteId: string;
    raceData: AthleteRaceContext;
    rankInfo?: AthleteRankContext;
    onBack?: () => void;
    backButtonLabel?: string;
    isGroupRide?: boolean;
    groupRideDetails?: GroupRideDetails;
    raceSector?: SpiritedSegment;
    sectorLeaderboardRow?: SectorLeaderboardRow;
    totalSectorContenders?: number;
    sectorDistanceMiles?: number | null;
    sectorElevationGainFt?: number | null;
}

const GeminiAthleteInsights: React.FC<GeminiAthleteInsightsProps> = ({
    athleteId,
    raceData,
    rankInfo,
    onBack,
    backButtonLabel,
    isGroupRide,
    groupRideDetails,
    raceSector,
    sectorLeaderboardRow,
    totalSectorContenders,
    sectorDistanceMiles,
    sectorElevationGainFt
}) => {
    const [isLoading, setIsLoading] = useState(true);
    const [insights, setInsights] = useState<string>('');
    const [error, setError] = useState<string | null>(null);
    const [photoUrl, setPhotoUrl] = useState<string | null>(null);
    const [userProfile, setUserProfile] = useState<UserProfile | null>(null);

    const hasFetchedRef = useRef<string | null>(null);
    const isFetchingRef = useRef(false);

    const athlete = useMemo(() => raceData.athletes.find(a => a.id === athleteId), [athleteId, raceData.athletes]);

    const sectorTelemetry = useMemo(() => {
        if (!athlete || !isGroupRide || !raceSector) return null;
        return getSectorAthleteTelemetry(
            athlete,
            raceSector,
            raceData.course.path,
            userProfile?.weightLbs
        );
    }, [athlete, isGroupRide, raceSector, raceData.course.path, userProfile?.weightLbs]);

    const executeFetch = async () => {
        if (!athlete || isFetchingRef.current) return;
        isFetchingRef.current = true;
        hasFetchedRef.current = athleteId;
        setIsLoading(true);
        setError(null);
        setInsights('');

        let profile: UserProfile | null = null;
        if (athlete.userId) {
            try {
                profile = await userService.getUserProfile(athlete.userId);
                setUserProfile(profile);
                if (profile?.photoURL) setPhotoUrl(profile.photoURL);
            } catch (e) {
                console.error("Failed to fetch photo/profile for insights", e);
            }
        }

        const effectiveSectorTelemetry = isGroupRide && raceSector
            ? getSectorAthleteTelemetry(
                athlete,
                raceSector,
                raceData.course.path,
                profile?.weightLbs
            )
            : null;

        const prompt = isGroupRide
            ? generateGroupRidePrompt(
                athlete,
                raceData,
                groupRideDetails,
                effectiveSectorTelemetry,
                sectorLeaderboardRow,
                totalSectorContenders,
                sectorDistanceMiles,
                sectorElevationGainFt,
                profile
            )
            : generateAthletePrompt(athlete, raceData, profile, rankInfo);

        try {
            const text = await cloudAnalysisService.generateAIInsights(prompt);
            setInsights(text);
            setError(null);
        } catch (e: any) {
            console.error("AI Generation failed:", e);
            const msg = e?.message || '';
            const isRateLimit = msg.includes('60 seconds') || msg.includes('wait') || e?.code === 'resource-exhausted' || e?.status === 429;
            setError(isRateLimit
                ? 'Rate limit reached. Please wait a moment before trying again.'
                : 'Failed to get insights from the AI. Please try again later.'
            );
            hasFetchedRef.current = null; // Allow user to retry
        } finally {
            setIsLoading(false);
            isFetchingRef.current = false;
        }
    };

    useEffect(() => {
        if (!athlete) {
            setError("Athlete data could not be found.");
            setIsLoading(false);
            return;
        }

        if (hasFetchedRef.current !== athleteId) {
            executeFetch();
        }
    }, [athleteId, athlete]);

    if (!athlete) {
        return (
            <div className="flex flex-col h-full">
                {onBack && (
                    <div className="mb-4">
                        <button onClick={onBack} className="flex items-center text-sm text-cyan-400 hover:text-cyan-300 transition-colors">
                            <ArrowLeftIcon className="w-4 h-4 mr-2" />
                            {backButtonLabel || 'Back to All Athletes'}
                        </button>
                    </div>
                )}
                <div className="flex-grow flex items-center justify-center text-red-400">
                    Error: Athlete not found.
                </div>
            </div>
        );
    }

    return (
        <div className="flex flex-col h-full">
            {onBack && (
                <div className="mb-4 flex-shrink-0">
                    <button onClick={onBack} className="flex items-center text-sm text-cyan-400 hover:text-cyan-300 transition-colors">
                        <ArrowLeftIcon className="w-4 h-4 mr-2" />
                        {backButtonLabel || 'Back to All Athletes'}
                    </button>
                </div>
            )}

            <div className="text-center mb-4 flex-shrink-0">
                <img
                    src={photoUrl || athlete.photoUrl || `https://api.dicebear.com/8.x/initials/svg?seed=${encodeURIComponent(athlete.name)}&backgroundColor=${(athlete.color || '#34d399').substring(1)}&size=96`}
                    alt={`${athlete.name}'s profile picture`}
                    className="w-24 h-24 rounded-full mx-auto border-4 object-cover"
                    style={{ borderColor: athlete.color || '#34d399' }}
                />
                <h2 className="mt-3 text-xl font-bold text-white">{athlete.name}</h2>
                <p className="text-md font-semibold text-gray-400">
                    {isGroupRide ? 'Group Ride & Sector Analysis' : 'AI Performance Analysis'}
                </p>
            </div>

            {/* Quick Sector Stat Ribbon for Group Rides */}
            {isGroupRide && sectorTelemetry && (
                <div className="mb-4 bg-gray-800/90 border border-amber-500/30 rounded-lg p-3 space-y-2 flex-shrink-0">
                    <div className="flex items-center justify-between">
                        <span className="text-xs font-bold text-amber-400 flex items-center gap-1.5">
                            <span>⚡</span> Race Sector Performance
                        </span>
                        {sectorLeaderboardRow && (
                            <span className="text-xs font-extrabold px-2 py-0.5 rounded bg-amber-500/20 border border-amber-500/40 text-amber-300">
                                P{sectorLeaderboardRow.rank} of {totalSectorContenders || sectorLeaderboardRow.rank}
                            </span>
                        )}
                    </div>
                    <div className="grid grid-cols-3 gap-2 pt-1 text-center border-t border-gray-700/60">
                        <div className="bg-gray-900/60 rounded p-1.5">
                            <div className="text-[11px] text-gray-400 font-medium">Sector Time</div>
                            <div className="text-sm font-bold text-white">{formatSectorTime(sectorTelemetry.effort.elapsedMs)}</div>
                        </div>
                        <div className="bg-gray-900/60 rounded p-1.5">
                            <div className="text-[11px] text-gray-400 font-medium">Avg Power</div>
                            <div className="text-sm font-bold text-white">
                                {sectorTelemetry.effort.avgPower ? `${Math.round(sectorTelemetry.effort.avgPower)}W` : '—'}
                            </div>
                            {sectorTelemetry.sectorAvgWkg && (
                                <div className="text-[10px] text-cyan-400">{sectorTelemetry.sectorAvgWkg} W/kg</div>
                            )}
                        </div>
                        <div className="bg-gray-900/60 rounded p-1.5">
                            <div className="text-[11px] text-gray-400 font-medium">Matches</div>
                            <div className="text-sm font-bold text-amber-400">
                                {sectorTelemetry.sectorMatches.length} <span className="text-[10px] text-gray-400 font-normal">in sector</span>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            <div className="flex-grow min-h-0 overflow-y-auto pr-2 -mr-2 text-gray-300 text-sm">
                {isLoading && (
                    <div className="flex flex-col items-center justify-center h-full text-center text-gray-400">
                        <svg className="animate-spin h-8 w-8 mb-3" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Generating {isGroupRide ? 'group ride insights' : 'insights with Gemini'}...
                    </div>
                )}
                {error && (
                    <div className="text-red-400 bg-red-900/40 border border-red-800/60 p-3 rounded-md mb-3 flex items-center justify-between gap-3">
                        <span className="text-xs leading-relaxed">{error}</span>
                        <button
                            onClick={() => {
                                hasFetchedRef.current = null;
                                executeFetch();
                            }}
                            className="px-2.5 py-1 bg-red-800/80 hover:bg-red-700 text-white rounded text-xs font-semibold whitespace-nowrap transition-colors"
                        >
                            Retry
                        </button>
                    </div>
                )}
                {insights && (
                    <div
                        className="prose prose-sm prose-invert max-w-none"
                        dangerouslySetInnerHTML={{ __html: renderMarkdown(insights) }}
                    />
                )}
            </div>
        </div>
    );
};

export default GeminiAthleteInsights;
